import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Home, Loader2, CheckCircle2, CreditCard, AlertTriangle, ArrowRight, ShieldCheck, MapPin, Receipt,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UI = {
  it: {
    title: "Tassa di soggiorno", loading: "Verifica della prenotazione…",
    errTitle: "Pagina non disponibile", errSub: "Contatta la struttura per assistenza.",
    stay: "Soggiorno", checkin: "Arrivo", checkout: "Partenza", guests: "Ospiti",
    amount: "Importo dovuto", pay: "Paga ora in sicurezza", paying: "Reindirizzamento al pagamento…",
    calc: "Calcolo della tassa di soggiorno in corso…", pending: "Pagamento in elaborazione…",
    paidTitle: "Tassa di soggiorno pagata", paidSub: "Grazie! Ti riportiamo al tuo Guest Portal…",
    exempt: "Sei esente dalla tassa di soggiorno.", notDue: "Nessuna tassa di soggiorno dovuta.",
    airbnbTitle: "Tassa inclusa nella prenotazione Airbnb", airbnbSub: "La tassa di soggiorno è già riscossa da Airbnb: non è richiesto alcun pagamento.",
    receipt: "Ricevuta", goPortal: "Vai al tuo Guest Portal", secure: "Pagamento sicuro con Stripe",
    failed: "Pagamento non riuscito. Riprova.", footer: "Powered by Urban OS · Urban Dreams Home",
  },
  en: {
    title: "City tax", loading: "Verifying your booking…",
    errTitle: "Page unavailable", errSub: "Please contact the property for assistance.",
    stay: "Stay", checkin: "Arrival", checkout: "Departure", guests: "Guests",
    amount: "Amount due", pay: "Pay now securely", paying: "Redirecting to payment…",
    calc: "Calculating the city tax…", pending: "Payment processing…",
    paidTitle: "City tax paid", paidSub: "Thank you! Taking you back to your Guest Portal…",
    exempt: "You are exempt from the city tax.", notDue: "No city tax due.",
    airbnbTitle: "Tax included in your Airbnb booking", airbnbSub: "The city tax is already collected by Airbnb: no payment required.",
    receipt: "Receipt", goPortal: "Go to your Guest Portal", secure: "Secure payment with Stripe",
    failed: "Payment failed. Please try again.", footer: "Powered by Urban OS · Urban Dreams Home",
  },
  es: {
    title: "Tasa turística", loading: "Verificando tu reserva…",
    errTitle: "Página no disponible", errSub: "Contacta con el alojamiento para asistencia.",
    stay: "Estancia", checkin: "Llegada", checkout: "Salida", guests: "Huéspedes",
    amount: "Importe a pagar", pay: "Pagar ahora de forma segura", paying: "Redirigiendo al pago…",
    calc: "Calculando la tasa turística…", pending: "Procesando el pago…",
    paidTitle: "Tasa turística pagada", paidSub: "¡Gracias! Te llevamos de vuelta a tu Guest Portal…",
    exempt: "Estás exento de la tasa turística.", notDue: "Sin tasa turística a pagar.",
    airbnbTitle: "Tasa incluida en tu reserva de Airbnb", airbnbSub: "La tasa turística ya la cobra Airbnb: no se requiere pago.",
    receipt: "Recibo", goPortal: "Ir a tu Guest Portal", secure: "Pago seguro con Stripe",
    failed: "Pago fallido. Inténtalo de nuevo.", footer: "Powered by Urban OS · Urban Dreams Home",
  },
};

const detectLang = () => { const b = (navigator.language || "it").slice(0, 2).toLowerCase(); return UI[b] ? b : "it"; };
const fmtDate = (v) => { try { return new Date(v).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return v; } };
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export default function TaxPage() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const [lang, setLang] = useState(detectLang());
  const t = UI[lang];
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [justPaid, setJustPaid] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/public/tax/${code}`); setD(r.data); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || "error"); }
    finally { setLoading(false); }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m); return () => { document.head.removeChild(m); };
  }, []);

  // ritorno dal pagamento Stripe
  useEffect(() => {
    const sess = params.get("tax_session");
    if (!sess) return;
    let tries = 0;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/public/portal/${code}/city-tax/status/${sess}`);
        if (r.data.payment_status === "paid") {
          setJustPaid(true); setParams({}, { replace: true }); await load();
          const portal = (d && d.portal_url) || `/p/${code}`;
          setTimeout(() => { window.location.href = portal.startsWith("http") ? portal : `${window.location.origin}${portal}`; }, 2800);
          return;
        }
        if (["expired", "failed", "canceled"].includes(r.data.status)) { setParams({}, { replace: true }); await load(); return; }
      } catch { /* ignore */ }
      if (++tries < 6) setTimeout(check, 2500); else { setParams({}, { replace: true }); load(); }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, code]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/public/portal/${code}/city-tax/checkout`, {
        origin_url: window.location.origin, success_path: `/tax/${code}`,
      });
      window.location.href = r.data.url;
    } catch (e) { setErr(e.response?.data?.detail || "error"); setPaying(false); }
  };

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a1020] text-slate-300">
      <div className="text-center"><Loader2 size={34} className="mx-auto animate-spin text-indigo-400" /><p className="mt-3 text-sm">{t.loading}</p></div>
    </div>
  );
  if (err) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a1020] px-6 text-center text-slate-300">
      <div data-testid="tax-error"><AlertTriangle size={40} className="mx-auto text-rose-400" /><p className="mt-3 text-lg font-semibold">{t.errTitle}</p><p className="mt-1 text-sm text-slate-500">{typeof err === "string" && err !== "error" ? err : t.errSub}</p></div>
    </div>
  );

  const tax = d.city_tax || {};
  const portalHref = (d.portal_url && d.portal_url.startsWith("http")) ? d.portal_url : `${window.location.origin}${d.portal_path || `/p/${code}`}`;
  const regularized = tax.regularized;

  return (
    <div className="min-h-screen bg-[#0a1020] pb-14 text-slate-100" style={{ backgroundImage: "radial-gradient(60% 40% at 50% 0%, rgba(79,70,229,0.18), transparent)" }}>
      <div className="mx-auto max-w-md px-4">
        <header className="flex items-center justify-between pt-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300"><Home size={15} /> {d.brand?.name || "Urban Dreams Home"}</div>
          <div className="flex gap-1 rounded-full bg-white/5 p-1" data-testid="tax-lang">
            {Object.keys(UI).map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase transition-colors ${lang === l ? "bg-white text-slate-900" : "text-slate-300 hover:bg-white/10"}`}>{l}</button>
            ))}
          </div>
        </header>

        <section className="mt-6" data-testid="tax-hero">
          <p className="text-sm font-medium text-indigo-300">{t.title}</p>
          <h1 className="mt-1 font-head text-3xl font-semibold leading-tight">{d.apartment?.name}</h1>
          {d.apartment?.zone && <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-400"><MapPin size={14} /> {d.apartment.zone}</p>}
        </section>

        <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div><div className="text-slate-500">{t.checkin}</div><div className="mt-0.5 font-medium">{fmtDate(d.check_in)}</div></div>
            <div><div className="text-slate-500">{t.checkout}</div><div className="mt-0.5 font-medium">{fmtDate(d.check_out)}</div></div>
            <div><div className="text-slate-500">{t.guests}</div><div className="mt-0.5 font-medium">{d.guests}</div></div>
          </div>
        </div>

        {/* Airbnb: tassa già riscossa */}
        {d.airbnb ? (
          <div className="mt-4 rounded-3xl border border-sky-400/25 bg-sky-500/10 p-5 text-center" data-testid="tax-airbnb">
            <ShieldCheck size={30} className="mx-auto text-sky-300" />
            <p className="mt-2 font-semibold text-sky-100">{t.airbnbTitle}</p>
            <p className="mt-1 text-xs text-sky-200/70">{t.airbnbSub}</p>
            <a href={portalHref} className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 text-sm font-semibold text-white hover:bg-white/15" data-testid="tax-go-portal"><ArrowRight size={16} /> {t.goPortal}</a>
          </div>
        ) : (justPaid || (regularized && ["paid", "collected_elsewhere"].includes(tax.status))) ? (
          <div className="mt-4 rounded-3xl border border-emerald-400/30 bg-emerald-500/10 p-5 text-center" data-testid="tax-paid">
            <CheckCircle2 size={34} className="mx-auto text-emerald-400" />
            <p className="mt-2 text-lg font-semibold text-emerald-200">{t.paidTitle}</p>
            {tax.receipt?.amount ? <p className="mt-1 flex items-center justify-center gap-1 text-xs text-emerald-300/80"><Receipt size={12} /> {t.receipt}: {eur(tax.receipt.amount)}</p> : null}
            <p className="mt-2 text-xs text-emerald-100/70">{t.paidSub}</p>
            <a href={portalHref} className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-600" data-testid="tax-go-portal"><ArrowRight size={16} /> {t.goPortal}</a>
          </div>
        ) : regularized ? (
          <div className="mt-4 rounded-3xl border border-emerald-400/25 bg-emerald-500/10 p-5 text-center" data-testid="tax-regularized">
            <CheckCircle2 size={30} className="mx-auto text-emerald-400" />
            <p className="mt-2 font-semibold text-emerald-100">{tax.status === "exempt" ? t.exempt : t.notDue}</p>
            <a href={portalHref} className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 text-sm font-semibold text-white hover:bg-white/15" data-testid="tax-go-portal"><ArrowRight size={16} /> {t.goPortal}</a>
          </div>
        ) : (
          <div className="mt-4 rounded-3xl border border-indigo-400/25 bg-white/[0.04] p-5 backdrop-blur-xl" data-testid="tax-pay-card">
            <div className="flex items-center gap-2 font-semibold text-slate-100"><CreditCard size={18} className="text-indigo-300" /> {t.title}</div>
            {tax.status === "not_calculated" ? (
              <p className="mt-3 flex items-center gap-2 text-sm text-slate-400"><Loader2 size={15} className="animate-spin" /> {t.calc}</p>
            ) : (
              <>
                <div className="mt-3 flex items-center justify-between rounded-2xl bg-white/[0.04] px-4 py-3">
                  <span className="text-sm text-slate-400">{t.amount}{tax.comune ? ` · ${tax.comune}` : ""}</span>
                  <span className="text-2xl font-bold text-indigo-200" data-testid="tax-amount">{eur(tax.amount_to_pay)}</span>
                </div>
                {tax.status === "payment_failed" && <p className="mt-2 text-xs text-rose-300">{t.failed}</p>}
                {tax.status === "payment_pending" ? (
                  <div className="mt-3 flex items-center justify-center gap-2 rounded-2xl border border-amber-400/25 bg-amber-500/10 py-3 text-sm text-amber-200"><Loader2 size={15} className="animate-spin" /> {t.pending}</div>
                ) : (
                  <button data-testid="tax-pay-btn" disabled={paying} onClick={pay} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98] disabled:opacity-40">
                    {paying ? <Loader2 size={17} className="animate-spin" /> : <CreditCard size={17} />} {paying ? t.paying : t.pay}
                  </button>
                )}
                <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck size={12} /> {t.secure}</p>
              </>
            )}
          </div>
        )}

        <p className="pt-8 text-center text-[11px] text-slate-600">{t.footer}</p>
      </div>
    </div>
  );
}
